import type {
    CompareBehaviorMetricsDto,
    CompareBiomassMetricsDto,
    CompareDataPointDto,
    CompareEnvironmentMetricsDto,
    CompareLiceMetricsDto,
    CompareMaturationMetricsDto,
    CompareMetricDto,
    CompareMetricsDto,
    CompareWoundsMetricsDto,
    LocationCompareResponseDto,
} from '@/services/types';
import {
    formatNumberByLocale,
    formatPercentageByLocale,
    formatSwimSpeedByLocale,
    formatTemperatureByLocale,
    formatUniversalUnitByLocale,
    type Locale,
} from '@/utils/localeFormatters';

import type { ApiWire } from '../shared/types';

import type {
    CompareBehaviorMetrics,
    CompareBiomassMetrics,
    CompareDataPoint,
    CompareEnvironmentMetrics,
    CompareLiceMetrics,
    CompareMaturationMetrics,
    CompareMetric,
    CompareMetrics,
    CompareWoundsMetrics,
    LocationCompareData,
    MetricFormatter,
} from './types';

// Only welfare ratios (0–1) need transforming to a 0–100 scale so the chart Y
// axis reads as a percentage. Everything else (grams, counts, bl/s) is consumed
// as-is by the chart and only the unit is added at format time.
const ratioToPercent = (v: number | null | undefined): number | null =>
    v == null ? null : v * 100;

// `localeFormatters` already validate the input and return their own fallback
// (`'-'`), so we just bind the unit/decimals here — no extra null guard needed.
const weightFormatter: MetricFormatter = (v, l) => formatUniversalUnitByLocale(v, 'g', l, 0);
const swimSpeedFormatter: MetricFormatter = (v, l) => formatSwimSpeedByLocale(v, l, 2);
const percentFormatter =
    (decimals: number): MetricFormatter =>
    (v, l) =>
        formatPercentageByLocale(v, l, decimals);
const numberFormatter =
    (decimals: number): MetricFormatter =>
    (v, l) =>
        formatNumberByLocale(v as number, l, decimals);

const identity = (v: number | null | undefined): number | null => v ?? null;

const mapDataPoint =
    (convert: (v: number | null | undefined) => number | null) =>
    (dto: ApiWire<CompareDataPointDto>): CompareDataPoint => {
        const values: Record<number, number | null> = {};
        for (const [cageId, raw] of Object.entries(dto.values ?? {})) {
            values[Number(cageId)] = convert(raw);
        }
        return { day: dto.day, values };
    };

const mapMetric = (
    dto: ApiWire<CompareMetricDto>,
    formatValue: MetricFormatter,
    convert: (v: number | null | undefined) => number | null = identity,
    beginAtZero?: boolean
): CompareMetric => ({
    dataPoints: (dto.dataPoints ?? []).map(mapDataPoint(convert)),
    formatValue,
    ...(beginAtZero !== undefined ? { beginAtZero } : {}),
});

function mapBiomass(dto: ApiWire<CompareBiomassMetricsDto>): CompareBiomassMetrics {
    return {
        weight: mapMetric(dto.weight, weightFormatter),
        conditionFactor: mapMetric(dto.conditionFactor, numberFormatter(2)),
        cv: mapMetric(dto.cv, numberFormatter(2)),
    };
}

function mapLice(dto: ApiWire<CompareLiceMetricsDto>): CompareLiceMetrics {
    return {
        female: mapMetric(dto.female, numberFormatter(3)),
        moving: mapMetric(dto.moving, numberFormatter(3)),
        caligus: mapMetric(dto.caligus, numberFormatter(3)),
    };
}

function mapWounds(dto: ApiWire<CompareWoundsMetricsDto>): CompareWoundsMetrics {
    const fmt = percentFormatter(2);
    return {
        any: mapMetric(dto.any, fmt, ratioToPercent),
        active: mapMetric(dto.active, fmt, ratioToPercent),
        healing: mapMetric(dto.healing, fmt, ratioToPercent),
        small: mapMetric(dto.small, fmt, ratioToPercent),
        medium: mapMetric(dto.medium, fmt, ratioToPercent),
        large: mapMetric(dto.large, fmt, ratioToPercent),
    };
}

function mapMaturation(dto: ApiWire<CompareMaturationMetricsDto>): CompareMaturationMetrics {
    const fmt = percentFormatter(3);
    return {
        fullyMature: mapMetric(dto.fullyMature, fmt, ratioToPercent),
    };
}

function mapBehavior(dto: ApiWire<CompareBehaviorMetricsDto>): CompareBehaviorMetrics {
    return {
        swimSpeed: mapMetric(dto.swimSpeed, swimSpeedFormatter),
    };
}

const oxygenConcentrationFormatter: MetricFormatter = (v, l) =>
    formatUniversalUnitByLocale(v, 'mg/L', l, 1);
const oxygenSaturationFormatter: MetricFormatter = (v, l) => formatPercentageByLocale(v, l, 1);
const temperatureFormatter: MetricFormatter = (v, l) => formatTemperatureByLocale(v, '°C', l, 1);
const salinityFormatter: MetricFormatter = (v, l) => formatUniversalUnitByLocale(v, 'PSU', l, 1);
const averageDepthFormatter: MetricFormatter = (v, l) => formatUniversalUnitByLocale(v, 'm', l, 1);

function mapEnvironment(dto: ApiWire<CompareEnvironmentMetricsDto>): CompareEnvironmentMetrics {
    const mapped: CompareEnvironmentMetrics = {};
    if (dto.oxygenConcentration) {
        mapped.oxygenConcentration = mapMetric(
            dto.oxygenConcentration,
            oxygenConcentrationFormatter,
            identity,
            false
        );
    }
    if (dto.oxygenSaturation) {
        mapped.oxygenSaturation = mapMetric(
            dto.oxygenSaturation,
            oxygenSaturationFormatter,
            identity,
            false
        );
    }
    if (dto.temperature) {
        mapped.temperature = mapMetric(dto.temperature, temperatureFormatter, identity, false);
    }
    if (dto.salinity) {
        mapped.salinity = mapMetric(dto.salinity, salinityFormatter, identity, false);
    }
    if (dto.averageDepth) {
        mapped.averageDepth = {
            ...mapMetric(dto.averageDepth, averageDepthFormatter, identity, true),
            reverse: true,
        };
    }
    return mapped;
}

export function mapCompareMetricsToDomain(dto: ApiWire<CompareMetricsDto>): CompareMetrics {
    const metrics: CompareMetrics = {};
    if (dto.biomass) metrics.biomass = mapBiomass(dto.biomass);
    if (dto.lice) metrics.lice = mapLice(dto.lice);
    if (dto.wounds) metrics.wounds = mapWounds(dto.wounds);
    if (dto.maturation) metrics.maturation = mapMaturation(dto.maturation);
    if (dto.behavior) metrics.behavior = mapBehavior(dto.behavior);
    if (dto.environment) {
        const environment = mapEnvironment(dto.environment);
        if (Object.keys(environment).length > 0) {
            metrics.environment = environment;
        }
    }
    return metrics;
}

export function mapLocationCompareResponseToDomain(
    dto: ApiWire<LocationCompareResponseDto>
): LocationCompareData {
    return {
        locationId: dto.locationId,
        locationName: dto.locationName ?? '',
        cages: (dto.cages ?? []).map((c) => ({
            id: c.id,
            name: c.name,
            // Kept absent rather than null when the request did not ask for fish groups.
            ...(c.activeFishGroup !== undefined && {
                activeFishGroup: c.activeFishGroup
                    ? { id: c.activeFishGroup.id, name: c.activeFishGroup.name }
                    : null,
            }),
        })),
        metrics: mapCompareMetricsToDomain(dto.metrics ?? {}),
    };
}

export type { Locale };
